import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { getCourtPageUrl, getEventUrl } from 'constants/pages';
import { BRAND_NAME, SITE_URL } from 'constants/products';
import { type EventPageFieldsFragment, EventStatusesEnum } from 'types/generated/server';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { getTournamentStaticDataById } from 'services/server/graphql/queries/getTournamentStaticDataById';
import { getTournamentStaticDataBySlug } from 'services/server/graphql/queries/getTournamentStaticDataBySlug';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { generateSportsEventJsonLd } from 'utils/shared/json-ld/sportsEvent';
import { isUuidFormatValid } from 'utils/shared/string/isUuidFormatValid';
import TournamentDetails, { PageProps } from 'screens/TournamentDetails';

/**
 * @note it's unclear if this is an option in Vercel so testing if it works
 * "Optional but recommended: use the Edge Runtime. This can only be done at the page level, not inside nested components."
 */
/**
 * @todo When comparing staging to production, prod was actually faster without this, but I'm unusure if it's the root cause. More analysis needed.
 */
// export const runtime = 'experimental-edge';

const REVALIDATE_IN_SECONDS = process.env.TARGET_PLATFORM === 'mobile' ? undefined : 30 * 60 * 1;
const IS_ALL_AS_ISR = true;

export const getStaticProps: GetStaticProps<PageProps, { idOrSlug: string }> = async ({
  params,
}) => {
  const idOrSlug = params?.idOrSlug;

  if (!idOrSlug) {
    console.error('No idOrSlug provided');
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    let event: EventPageFieldsFragment | null = null;

    if (isUuidFormatValid(idOrSlug)) {
      const eventByPk = await getTournamentStaticDataById({ id: idOrSlug });

      if (eventByPk?.eventsByPk) {
        event = eventByPk.eventsByPk;
      }
    }

    if (!event) {
      const eventBySlug = await getTournamentStaticDataBySlug({ slug: idOrSlug });
      event = eventBySlug?.events?.[0] || null;
    }

    if (!event) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    const faqs = event.faqs?.length > 0 ? event.faqs : [];
    const addressFromCity =
      event?.city && event.city?.name && event.city?.countrySubdivision?.name
        ? `${event.city.name}, ${event.city.countrySubdivision.name}`
        : '';
    const addressFromVenue = event.venue?.addressString || '';

    const eventStatus =
      !!event.canceledAt || event.status === EventStatusesEnum.Canceled
        ? 'EventCancelled'
        : 'EventScheduled';
    const registrationPrice = event.registrationPriceUnitAmount ?? 0;
    const offers =
      registrationPrice > 0
        ? {
            price: (registrationPrice / 100).toFixed(2),
            priceCurrency: event.currency || 'USD',
            availability: 'InStock',
            url: `${SITE_URL}${getEventUrl(event.slug || event.id)}`,
          }
        : undefined;

    const sportsEventSchema = generateSportsEventJsonLd({
      name: event?.title || '',
      sport: 'Pickleball', // @todo: get sport from event
      description: event?.description || '',
      startDate: event?.startDateTime || '',
      endDate: event?.endDateTime || '',
      eventStatus,
      eventAttendanceMode: 'OfflineEventAttendanceMode',
      url: `${SITE_URL}${getEventUrl(event.slug || event.id)}`,
      image: event?.coverImageUrl
        ? getImageUrl({
            url: event.coverImageUrl,
            path: event.coverImagePath || '',
          })
        : undefined,
      location: {
        name: event?.venue?.title || '',
        url: event?.venue?.slug ? `${SITE_URL}${getCourtPageUrl(event.venue.slug)}` : '',
        address:
          event.addressString || addressFromVenue || addressFromCity || event.displayLocation || '',
        geo: {
          latitude: event?.geometry?.coordinates[1] || '',
          longitude: event?.geometry?.coordinates[0] || '',
        },
      },
      offers,
      organizer: {
        '@type': 'Organization',
        name: event.sourceOrganizerTitle || BRAND_NAME,
        url: SITE_URL,
      },
    });

    const faqSchema =
      faqs.length > 0
        ? generateFaqJsonLd({
            items: faqs,
          })
        : undefined;

    const combinedSchemas = combineJsonLdSchemas([sportsEventSchema, faqSchema].filter((s) => !!s));

    return {
      props: { event, faqs: faqs, jsonLd: JSON.stringify(combinedSchemas) },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    console.log('build error', error);
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (IS_ALL_AS_ISR) {
    return {
      paths: [],
      fallback: true,
    };
  }

  /**
   * @todo generate list of events
   */
  const paths: string[] = [];

  return {
    paths,
    fallback: true,
  };
};

export default TournamentDetails;
